import PropTypes from 'prop-types'
import { Package } from 'react-feather'
import CartItemsList from '../../Cart/CartItemsList'
import {
  organizeRatesByCarrier,
  buildGroupCartItems,
  isFreeShippingRate
} from './shippingGroupsHelpers'
import ShippingRateOption from './ShippingRateOption'

const ShippingGroup = ({ group, groupIndex, totalGroups, selectedRate, onRateSelect, cartItems }) => {
  // Use line_items from server response (includes group-specific quantity/address)
  const groupCartItems = buildGroupCartItems(group.line_items, cartItems)

  const hasRates = group.rates && group.rates.length > 0
  const isFreeShipping = !hasRates && isFreeShippingRate(selectedRate)
  const hasDestinationAddress = !!group.destination_address

  // Only organize rates if they exist
  const ratesByCarrier = hasRates ? organizeRatesByCarrier(group.rates) : {}
  const carrierNames = Object.keys(ratesByCarrier)

  return (
    <div className="shipping-group mb-4">
      <div className="shipping-group-header mb-3">
        <h5>
          <Package size={20} className="mr-2" />
          Shipping Group {groupIndex + 1} of {totalGroups}
        </h5>
      </div>

      {groupCartItems.length > 0 && (
        <div className="shipping-group-items mb-3">
          <CartItemsList
            isCheckout={true}
            filteredCartItems={groupCartItems}
            hideMultiAddressDisplay={hasDestinationAddress}
            groupDestinationAddress={group.destination_address}
          />
        </div>
      )}

      {!hasRates && !isFreeShipping && (
        <div className="alert alert-info mb-3">
          <small>⏳ Calculating shipping rates for this group...</small>
        </div>
      )}

      {isFreeShipping && (
        <div className="alert alert-success mb-3">
          <strong>Free Shipping</strong> — No shipping charges for this shipment
        </div>
      )}

      {hasRates &&
        carrierNames.map(carrierName => (
          <div key={carrierName} className="carrier-section mb-3">
            <div className="carrier-header mb-2">
              <h6 className="text-muted">{carrierName}</h6>
            </div>
            {ratesByCarrier[carrierName].map(rate => (
              <ShippingRateOption
                key={rate.service_code}
                rate={rate}
                isSelected={selectedRate?.service_code === rate.service_code}
                onSelect={onRateSelect}
              />
            ))}
          </div>
        ))}
    </div>
  )
}

ShippingGroup.propTypes = {
  group: PropTypes.shape({
    carrier_service_group_id: PropTypes.string.isRequired,
    line_items: PropTypes.arrayOf(
      PropTypes.shape({
        cart_item_id: PropTypes.number.isRequired,
        quantity: PropTypes.number.isRequired,
        customer_address_id: PropTypes.number
      })
    ).isRequired,
    destination_address: PropTypes.shape({
      id: PropTypes.number,
      full_name: PropTypes.string,
      address_line_1: PropTypes.string,
      address_line_2: PropTypes.string,
      city: PropTypes.string,
      state: PropTypes.string,
      zip: PropTypes.string
    }),
    rates: PropTypes.arrayOf(
      PropTypes.shape({
        service_name: PropTypes.string.isRequired,
        service_code: PropTypes.string.isRequired,
        carrier_name: PropTypes.string.isRequired,
        cost_in_cents: PropTypes.number.isRequired,
        estimated_days: PropTypes.number.isRequired,
        encrypted_cost: PropTypes.string.isRequired,
        carrier_service_id: PropTypes.number.isRequired
      })
    ).isRequired
  }).isRequired,
  groupIndex: PropTypes.number.isRequired,
  totalGroups: PropTypes.number.isRequired,
  selectedRate: PropTypes.shape({
    service_code: PropTypes.string,
    cost_in_cents: PropTypes.number,
    encrypted_cost: PropTypes.string,
    carrier_service_id: PropTypes.number,
    cart_item_ids: PropTypes.arrayOf(PropTypes.number),
    is_free_shipping: PropTypes.bool
  }),
  onRateSelect: PropTypes.func.isRequired,
  cartItems: PropTypes.array.isRequired
}

export default ShippingGroup
